import { procentStap } from './leerkaart';
import type { EigenWekenoverzicht, WerksoortSleutel } from './work-quality.api';

/**
 * Eén cel van de tab Maand: één week, één werksoort (`trend-en-maand.md`, Tab Maand, "Cel zonder cijfers").
 * Alleen `cijfers` draagt een "in één keer goed"; onder de grens blijft alleen het aantal beoordeeld, als de
 * dienst het meestuurt.
 */
export type MaandCel =
  | { soort: 'cijfers'; beoordeeld: number; goed: number; percentage: number }
  | { soort: 'teWeinig'; beoordeeld?: number }
  | { soort: 'nietNagekeken' }
  | { soort: 'geenGegevens' };

const GEEN: MaandCel = { soort: 'geenGegevens' };

const isAantal = (waarde: unknown): waarde is number => Number.isSafeInteger(waarde) && (waarde as number) >= 0;
const isObject = (waarde: unknown): waarde is Record<string, unknown> => typeof waarde === 'object' && waarde !== null;

/**
 * De cel voor `weekKey` en `werksoort`, uit het hoofdcijfer `inEenKeerGoed` (SPEC-werkkwaliteit-herontwerp
 * story 9). De grens toetst de dienst al; deze cel leest alleen wat er staat. Een week die ontbreekt, een
 * week of werksoort zonder hoofdcijfer, en elke vorm die het portaal niet kent is "geen gegevens", per cel.
 */
export function maandCel(overzicht: EigenWekenoverzicht, weekKey: string, werksoort: WerksoortSleutel): MaandCel {
  const week = overzicht.weken.find((w) => w.weekKey === weekKey);
  if (week === undefined || week.reden !== undefined) return GEEN;
  const cel: unknown = week.werksoorten?.[werksoort];
  if (!isObject(cel)) return GEEN;
  if (cel.reden === 'geen_beoordeeld_werk') return { soort: 'nietNagekeken' };

  const cijfer = cel.inEenKeerGoed;
  if (!isObject(cijfer)) return GEEN;
  const { reden, aantalBeoordeeld: beoordeeld, aantalInEenKeerGoed: goed, percentage } = cijfer;
  if (reden === 'te_weinig_gegevens') return isAantal(beoordeeld) ? { soort: 'teWeinig', beoordeeld } : { soort: 'teWeinig' };
  if (reden === 'niet_nagekeken') return { soort: 'nietNagekeken' };
  if (reden !== undefined) return GEEN;
  if (!isAantal(beoordeeld) || !isAantal(goed) || goed > beoordeeld || !isAantal(percentage) || percentage > 100) return GEEN;
  return { soort: 'cijfers', beoordeeld, goed, percentage };
}

/**
 * De breedte van een staaf "in één keer goed" in stappen van vijf procent, uit het percentage van de dienst:
 * 0 goed is geen staaf, meer dan 0 minstens vijf, minder dan alles hoogstens 95, zodat een staaf nooit als
 * "alles goed" of "niets" leest als dat niet zo is. Vergelijkingen, geen deling.
 */
export function maandStaafBreedte(cel: Extract<MaandCel, { soort: 'cijfers' }>): number {
  if (cel.goed <= 0) return 0;
  const stap = Math.max(5, procentStap(cel.percentage));
  return cel.goed < cel.beoordeeld ? Math.min(95, stap) : stap;
}
